import { STATUS, StoredFile } from 'utils/interfaces';
import { PUBLISH_STATUSES } from 'constants/status.constants';

export enum INGESTION_STATUS {
  UNKNOWN = 0,
  COMPLETED = 1,
  GENERIC_ERROR = 2,
  REQUEST_RECEIVED = 3,
  EVENT_SENT = 4,
  PROCESSING = 5,
  STORING = 6,
  INVALID_SEARCH = 7,
  STORAGE_ERROR = 8,
  NO_DOCUMENT_MET_THRESHOLD = 9,
  TOO_MANY_RESULTS = 10,
  EXECUTION_ERROR = 11,
  NO_RESULTS_FROM_SITE = 12,
  REPORTING_IN_PROCESS_TO_REQUESTOR = 13,
  WAITING_FOR_CONCURRENCY_CONDITIONS = 14,
  RESPONSE_RECEIVED = 15,

  // direct ingestion errors
  DIRECT_INGESTION_ERROR = 100,
  DIRECT_INGESTION_FORM_SUBMISSION = 101,
  DIRECT_INGESTION_CAPTCHA = 102,
  DIRECT_INGESTION_SEARCH_INFO = 103,
  DIRECT_INGESTION_INDIVIDUAL_DETAILS = 104,
  DIRECT_INGESTION_SCREENSHOT = 105,
  DIRECT_INGESTION_PARSE_INFO = 106,
  DIRECT_INGESTION_BOT_DETECTION = 107,
  DIRECT_INGESTION_VALIDATION_ERROR = 108,
  DIRECT_INGESTION_CONNECTION_ERROR = 109,
  DIRECT_INGESTION_SITE_RESOURCE_NOT_FOUND = 110,
}
export enum INGESTION_SOURCE_TYPE {
  UNKNOWN = 0,
  PARTNER = 1,
  BULK = 2,
  DIRECT = 3,
  MONITORING = 4,
  PROMPT_TEST = 5,
}

export interface PsvIngestionDataProxySettings {
  proxyId?: number;
  proxyKey: string;
  statusId: STATUS;
}

export interface PsvIngestionDataCaptchaSettings {
  captchaKey: string;
  captchaTypeKey: string;
}

export enum INGESTION_ENGINE {
  CODE = 'CODE',
  DB_SDI = 'DB_SDI',
}

export enum BROWSER_SOURCE_SETTING {
  LOCAL = 1,
  STATIC_BROWSER_WS_ENDPOINT = 2,
  DYNAMIC_BROWSER_WS_ENDPOINT = 3,
}

export interface PsvIngestionData {
  acquisitionConcurrencyConfigId?: number;
  acquisitionConcurrencyConfigLabel?: string;
  ingestionId: number;
  ingestionKey: string;
  ingestionEngine: INGESTION_ENGINE;
  label?: string;
  ingestionSourceType: INGESTION_SOURCE_TYPE;
  browserSourceSetting: BROWSER_SOURCE_SETTING;
  url: string;
  screenshotUrl?: string;
  captchaRequired: boolean;
  loginUsername?: string;
  loginPassword?: string;
  statusId: STATUS;
  captchas?: PsvIngestionDataCaptchaSettings[];
  proxies?: PsvIngestionDataProxySettings[];
  overrideDuplicateUrlWarning?: boolean;
  publishStatusId: PUBLISH_STATUSES;
  publishStatusLabel?: string;
}

export interface PsvIngestionMeta {
  totalItems: number;
  itemsPerPage: number;
  itemsCount: number;
  currentPage: number;
  totalPages: number;
  sortBy: string[];
}

export interface PsvIngestionResponse {
  data: PsvIngestionData[];
  meta: PsvIngestionMeta;
}

export interface PsvAttributeRequestData {
  psvCertemyAttributeKey: string;
  attributeType: string;
  fieldName: string;
  label: string;
  sourceTable: string;
  sourceColumn: string;
  statusId: STATUS;
}

export interface PsvAttributeResponseData {
  label: string;
  fieldName: string;
}

export interface PsvAttributeData {
  requestAttributes: PsvAttributeRequestData[];
  responseAttributes: PsvAttributeResponseData[];
  // responseAttributes: any;
}

export interface PsvIngestionFilters {
  page?: number;
  limit?: number;
  sortBy?: number;
  searchBy?: string;
  search?: string;
  filter?: { [column: string]: string | string[] };
  path: string;
}

export interface PsvIngestionPageSettings {
  isShadowDomSupportRequired?: boolean;
  bypassCSP: boolean;
  defaultTimeout: number;
}

export interface PsvIngestionTermSettings {
  click?: string;
}

export interface PsvIngestionWaitForSelectorSettings {
  value?: string;
  timeout?: number;
  visible?: boolean;
}

export interface PsvIngestionFormSwitchSettings {
  fieldName?: string;
  click?: string;
  waitForNavigation?: boolean;
  waitForSelector?: PsvIngestionWaitForSelectorSettings;
}

export interface PsvIngestionPreloadSettings {
  click: string;
  waitForNavigation: boolean;
}

export interface Transformations {
  index?: number;
  method?: string;
  args?: any[];
  dateParseFormat?: string;
}

export interface PsvIngestionSearchSettings {
  waitForSelector?: PsvIngestionWaitForSelectorSettings;
  psvCertemyAttributeKey?: string;
  fieldName: string;
  label: string;
  value?: string;
  fieldType?: string;
  defaultValue?: string;
  waitForNavigation?: boolean;
  transformations?: Transformations[];
  isRequired?: boolean;
}

export interface PsvIngestionLookupSettings {
  not?: boolean;
  fieldName?: string;
  value?: string;
}

export interface PsvIngestionSubmitSettings {
  value?: string;
  lookup: PsvIngestionLookupSettings[];
  keyPress?: KeyboardPress;
  withFunction?: PsvIngestionSubmitFunctionSettings;
}

export interface PsvIngestionSubmitFunctionSettings {
  value: string;
}

export interface PayloadActionMappingField {
  id: string;
  fieldName: string;
  not?: boolean;
}

export interface KeyboardTextInput {
  isStatic: boolean;
  selector?: string;
  staticValue?: string;
  fieldName?: string;
  transformations?: Transformations[];
}

export interface KeyboardPress {
  key: string;
  delay?: number;
}

export interface PayloadActionMapping {
  fields?: PayloadActionMappingField[];
  click?: string;
  keyboardTextInput?: KeyboardTextInput;
  keyPress?: KeyboardPress;
  waitForSelector?: PsvIngestionWaitForSelectorSettings;
  waitForNavigation?: boolean;
  type: string;
}

export interface PsvIngestionPayloadDrivenAction {
  payloadActionMap?: PayloadActionMapping[];
}

export interface CaptchaImageSettings {
  phrase?: boolean;
  case?: boolean;
  numeric?: number;
  math?: boolean;
  minLength?: number;
  maxLength?: number;
  comment?: string;
}

export interface PsvIngestionSettingsCaptcha {
  usePuppeteerRecaptchaPlugin?: boolean;
  verifyButtonSelector?: string;
  captchaImageSelector?: string;
  captchaImageTextResponseSelector?: string;
  captchaImageSettings?: CaptchaImageSettings;
  solveCaptchaAfterSubmit?: boolean;
}

export interface PsvIngestionRequestSettings {
  waitForSelector: PsvIngestionWaitForSelectorSettings;
  terms?: PsvIngestionTermSettings;
  formSwitch: PsvIngestionFormSwitchSettings;
  search: PsvIngestionSearchSettings[];
  preSearchActions?: PsvIngestionPayloadDrivenAction[];
  submit: PsvIngestionSubmitSettings;
  captcha?: PsvIngestionSettingsCaptcha;
}

export interface PsvIngestionResponseFieldSettings {
  fieldName: string;
  certemyFieldName: string;
}

export interface PsvIngestionDataSelectorSettings {
  value?: string;
  timeout?: number;
  visible?: boolean;
  textContent?: string[];
}

export interface PsvIngestionDataSelectorSettingsWithTextFilter extends PsvIngestionDataSelectorSettings {
  textContent?: string[];
}

export interface PsvIngestionDataSelectorSettingsIncludeNavigation extends PsvIngestionDataSelectorSettings {
  withoutNavigation: boolean;
  waitForSelector: PsvIngestionDataSelectorSettings;
}

export interface PsvIngestionLinkSelectorSettings {
  value?: string;
  type?: string;
  useBrowserQL?: boolean;
}

export interface PsvIngestionMappingSettings {
  fieldName?: string;
  value?: string[];
  joinChar?: string;
  isInExpandedContent?: boolean;
  transformations?: ResponseFieldTransformation[];
  payloadReference?: string;
}

export interface ResponseFieldTransformation {
  index?: number;
  method?: string;
  args?: any[];
}

export interface PsvIngestionDiscipline {
  presenceMeansAffirmative: boolean;
  selector: PsvIngestionDataSelectorSettingsWithTextFilter;
}
export interface PsvIngestionResponseResultsSettings {
  dataSelector: PsvIngestionDataSelectorSettingsIncludeNavigation;
  linkSelector: PsvIngestionLinkSelectorSettings;
  collapseSelector?: PsvIngestionDataSelectorSettings;
  confirmSelectionSelector?: PsvIngestionDataSelectorSettings;
  skippedToDetailsSelector?: PsvIngestionDataSelectorSettings;
  noResultsSelector?: PsvIngestionDataSelectorSettingsWithTextFilter;
  validationMessageSelector?: PsvIngestionDataSelectorSettingsWithTextFilter;
  mapping: PsvIngestionMappingSettings[];
  discipline?: PsvIngestionDiscipline;
  changeTemplateCode?: PsvIngestionTemplateCodeChanges[];
}

export interface PsvIngestionTemplateCodeChanges {
  selector: string;
  elementToVariable?: string;
  actionName: string;
  actionProps: string[];
  attribute?: string;
  transformations?: Transformations[];
}

export interface PsvIngestionResponseDetailsSettings {
  dataSelector: PsvIngestionDataSelectorSettingsIncludeNavigation;
  rowSelector?: PsvIngestionLinkSelectorSettings;
  backToResults?: PsvIngestionFormSwitchSettings;
  mapping: PsvIngestionMappingSettings[];
  discipline?: PsvIngestionDiscipline;
  changeElementMarkup?: PsvIngestionElementMarkupChanges;
}

export type PsvIngestionElementMarkupChanges = {
  initialSelector: string;
  rowSelector: string;
  rowsNumber: number;
  newParentSelector?: string;
  newSectionSelector?: string;
  sliceFromTop?: number;
  sliceFromBottom?: number;
};

export interface PsvIngestionResponseSettings {
  results: PsvIngestionResponseResultsSettings;
  details: PsvIngestionResponseDetailsSettings;
}

export interface PsvIngestionResponsePrintLinkSettings {
  selector: string;
  attribute: string;
  transformations: Transformations[];
}
export interface PsvIngestionResponseScreenshotSettings {
  printLink?: PsvIngestionResponsePrintLinkSettings;
  captureBeyondViewport: boolean;
}

interface PsvIngestionBrowserSettings {
  browserLaunchArgs?: string[];
  browserWebSocketUrl?: string;
}

export interface PsvIngestionSettings {
  ingestionKey?: string;
  ingestionSettingsKey?: string;
  browser?: PsvIngestionBrowserSettings;
  page?: PsvIngestionPageSettings;
  request?: PsvIngestionRequestSettings;
  response?: PsvIngestionResponseSettings;
  screenshot?: PsvIngestionResponseScreenshotSettings;
}

export interface PsvIngestionSettingsResponse {
  ingestionSettings: PsvIngestionSettings;
}

export interface PsvAcquisitionRequestTrackingHistoryResponse {
  data: PsvAcquisitionRequestTrackingData[];
}

export interface PsvAcquisitionRequestTrackingResponse {
  data: PsvAcquisitionRequestTrackingData;
}

export interface PsvAcquisitionRequestTrackingData {
  acquisitionRequestTrackingHistoryId?: number;
  psvAcquisitionRequestId: string;
  acquisitionRequestStatusId: INGESTION_STATUS;
  resultsFound?: number;
  ipv4Address?: string;
  ipv6Address?: string;
  credentialSourceKey?: string;
  ingestionKey?: string;
  searchTimeMs?: number;
  totalTimeMs?: number;
  logTime?: string;
  eventSent?: string;
  eventReceived?: string;
  eventProcessed?: string;
  acquisitionRequest?: string;
  statusId?: STATUS;
  rcu?: string;
  rcd?: string;
  rmu?: string;
  rmd?: string;
}

export interface PsvLicenseTypeData {
  licenseType: string;
  credentialSourceKey: string;
}

export interface PsvBoardData {
  licensingBoard: string;
  licenseTypeData: PsvLicenseTypeData[];
}

export interface PsvCredentialSourceActiveBoardsResponse {
  licensingBoardData: PsvBoardData[];
}

export interface PsvCredentialSourcesResponse {
  credentialSourceId: number;
  credentialSourceKey: string;
  ingestionId: number;
  country?: string;
  state?: string;
  licensingBoard: string;
  licensingBoardValue: string;
  licenseType: string;
  licenseTypeValue: string;
  statusId: STATUS;
  ingestion?: PsvIngestionData;
}

export interface PsvSourceRequest {
  key: string;
  ingestionEngine?: string;
}

export interface PsvSearchRequest {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  businessName?: string;
  licenseNumber?: string;
  licenseStatus?: string; // optional

  city?: string; // optional
  county?: string; // optional
  state?: string; // optional
  postalCode?: string; // optional
  country?: string; // optional
  addressLine1?: string; // optional
  addressLine2?: string; // optional
  dateOfBirth?: string;
  socialSecurityNumber?: string;
  employerIdentificationNumber?: string;
  nationalProviderIdentificationNumber?: string;
  licenseeType?: string;
}

export interface PsvAcquisitionRequest {
  source: PsvSourceRequest;
  search: PsvSearchRequest;
}

export interface AcquisitionIndividual {
  rawName?: string; // optional
  firstName?: string; // optional
  middleName?: string; // optional
  lastName?: string; // optional
  businessName?: string;
  dateOfBirth?: string;
  displayName?: string;
  addresses?: AcquisitionAddress[];
}

export interface AcquisitionAddress {
  rawAddress?: string; // optional
  city?: string; // optional
  county?: string; // optional
  state?: string; // optional
  postalCode?: string; // optional
  country?: string; // optional
}

export interface AcquisitionVerification {
  rawLicenseStatus?: string; // optional
  licenseStatus: string;
  licenseId?: string; // optional
  licenseNumber: string;
  licenseBoard: string;
  licenseType: string;
  issueDate: string;
  effectiveDate: string;
  expirationDate: string;
  storedFiles?: StoredFile[]; // list, optional
  verificationDate: string;
}

interface AcquisitionDisciplineDetails {
  disciplineFiles: StoredFile[];
}

export interface AcquisitionDiscipline {
  boardAction?: string;
  disciplineDetails?: AcquisitionDisciplineDetails[];
}

export interface PsvAcquisitionResponse {
  confidenceScore: string;
  individual: AcquisitionIndividual;
  verification: AcquisitionVerification;
  discipline: AcquisitionDiscipline;
}

export interface PsvSearchDetails {
  type: string;
  parameters: PsvSearchDetailParameters[];
  minimumConfidenceScore: number;
  minimumScoreGap: number;
}

export interface PsvSearchDetailParameters {
  parameterName: string;
  threshold: number;
  boost: string;
  parameterValue: string;
  exactMatch: boolean;
  alogorithm: string;
  analyzer: string;
}

export interface PsvCompleteResponse {
  psvAcquisitionRequestId: string;
  searchDetails: PsvSearchDetails;
  documents: PsvAcquisitionResponse[];
}

export interface PsvLicenseInfo {
  captchaRequired: boolean;
  clickToShow: string;
  credentialSourceStatusId: STATUS;
  ingestionStatusId: STATUS;
  ingestionKey: string;
  licenseBoardSelector: string;
  licenseBoardOptionSelector: string;
  licenseTypeSelector: string;
  licenseTypeOptionSelector: string;
  template: string;

  licenseInfoId?: number;
  country?: string;
  label?: string;
  loginPassword?: string;
  loginUsername?: string;
  screenshotUrl?: string;
  state?: string;
  url?: string;

  boardSelectionSubmitSelector?: string; // - optional
  searchButtonSelector?: string; // - optional
}

export interface PsvLicenseInfoProvisionResponse {
  statusTrackerId: string;
}

export interface PsvLicenseInfoTrackResponse {
  statusTrackerId: string;
  statusTrackerKey?: string;
  trackerArea: string;
  trackedStatus: string;
  statusId: STATUS;
  meta: {
    [key: string]: any;
    licenseInfoSettings?: { [key: string]: any }[];
    licenseInfoTracker?: { [key: string]: PsvLicenseInfoTrackCounts };
  };
  rcu?: string;
  rcd?: string;
  rmu?: string;
  rmd?: string;
}

export interface PsvLicenseInfoTrackCounts {
  error: boolean;
  processed: number;
  total: number;
}

export interface ExtensionCredentialData {
  licenseType?: string;
  licenseBoard?: string;
}

export interface ExtensionCredential {
  credential: ExtensionCredentialData;
}
